import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConstants } from 'src/app/commons/app-constants';
import { isPlanFeatureBlock, quietPlanBlockContext } from 'src/app/interceptors/quiet-plan-block';
import { InventoryLotService } from 'src/app/modules/inventory/services/inventory-lot.service';
import { PendingStockInGroup, StockGroup, StockGroupFilter } from 'src/app/modules/inventory/model/inventory.model';
import { DimensionConstraint, DimensionField, QuickSearchQuery, QuickSearchTenantDefaults } from './quick-search-parser';
import { QUICK_SEARCH_PURCHASING_FLAGS } from './quick-search-purchasing-flags';

export interface FloorNowRow {
  runEncodedId: string;
  runCode: string;
  operatorName: string | null;
  machineName: string | null;
  millSiteName: string | null;
  productName: string | null;
  status: string;
  startedAt: string | null;
  /** 2026-09-15 — Floor Now: when the job was created (drafts' waiting time) / paused. */
  createdDate?: string | null;
  pausedAt?: string | null;
}

export interface UpcomingConsignmentRow {
  encodedId: string;
  blNumber: string | null;
  shipper: string | null;
  consignee: string | null;
  date: string | null;
  status: string | null;
  /** Container / truck numbers (first three); absent without the Tally right. */
  containers?: string[] | null;
  containerCount?: number;
}

export interface ConsignmentRadarResult {
  preparing: UpcomingConsignmentRow[];
  departing: UpcomingConsignmentRow[];
  arriving: UpcomingConsignmentRow[];
}

export interface QuickSearchResult {
  available: StockGroup[];
  inProcess: FloorNowRow[];
  arriving: ConsignmentRadarResult | null;
  /** "PENDING STOCK-IN" — tallied but never received into a real InventoryLot. Runs alongside
   *  `available` (not gated on productName) since a bare dimension search ("girth 90cm") is just
   *  as meaningful here as a species search. */
  pendingStockIn: PendingStockInGroup[];
  /** Sources the company's plan does not include (left out of this search, no dialog): 'stock' = Inventory (Available
   *  Now and Pending Stock-In), 'mill' = Mill Jobs (Floor Now). The panel shows one quiet "needs a higher plan" line. */
  lockedSources?: ('stock' | 'mill')[];
}

const DEFAULT_SQUARE_TOLERANCE_PERCENT = 5;
const DEFAULT_ROUND_TOLERANCE_PERCENT = 8;

@Injectable({ providedIn: 'root' })
export class QuickSearchService {
  private base = `${AppConstants.API_URL}api/v1`;

  constructor(private http: HttpClient, private lotService: InventoryLotService) {}

  /** A source the plan does not include (403 PLAN_FEATURE_DISABLED) is left out of the results and remembered in `locked`,
   *  so the search still shows everything the plan does include. Any other error still fails the search as before. */
  private skipIfPlanLocked<T>(source$: Observable<T>, empty: T, source: 'stock' | 'mill', locked: Set<'stock' | 'mill'>): Observable<T> {
    return source$.pipe(catchError(err => {
      if (isPlanFeatureBlock(err)) {
        locked.add(source);
        return of(empty);
      }
      return throwError(() => err);
    }));
  }

  search(query: QuickSearchQuery, tenantDefaults?: QuickSearchTenantDefaults | null): Observable<QuickSearchResult> {
    const locked = new Set<'stock' | 'mill'>();
    const available$ = this.skipIfPlanLocked(this.searchAvailable(query, tenantDefaults), [] as StockGroup[], 'stock', locked);
    const pendingStockIn$ = this.skipIfPlanLocked(this.searchPendingStockIn(query, tenantDefaults), [] as PendingStockInGroup[], 'stock', locked);

    // Floor Now and Consignment Radar have no dimension data at all — species is the only field
    // that connects them to a search. Without a resolved species, skip them rather than return
    // every active run / every upcoming consignment unfiltered, which isn't what was searched for.
    if (!query.productName) {
      return forkJoin({ available: available$, pendingStockIn: pendingStockIn$ }).pipe(
        map(({ available, pendingStockIn }) => ({
          ...filterByType({ available, inProcess: [], arriving: null, pendingStockIn }, query.isRound),
          lockedSources: [...locked],
        })));
    }

    const inProcess$ = this.skipIfPlanLocked(this.http.get<FloorNowRow[]>(`${this.base}/processing-runs/floor-now`, {
      params: new HttpParams().set('productName', query.productName),
      context: quietPlanBlockContext(),
    }), [] as FloorNowRow[], 'mill', locked);
    const arriving$ = this.http.get<ConsignmentRadarResult>(`${this.base}/dashboard-v3/consignment-radar`, {
      params: new HttpParams().set('windowDays', '14').set('productName', query.productName),
    });

    return forkJoin({ available: available$, inProcess: inProcess$, arriving: arriving$, pendingStockIn: pendingStockIn$ }).pipe(
      map(result => ({ ...filterByType(result, query.isRound), lockedSources: [...locked] })));
  }

  /** Ambiguous sizes (a lone number+unit with no field word, e.g. "200mm") never got a hardcoded
   *  plausibility range — real signal only. If the matched species already tells us Round vs
   *  Square, that alone decides which field to check. If not, check every plausible field
   *  (width, thickness, girth) at once and let real stock answer the question — no field is
   *  silently favored, and a wrong candidate just contributes zero results, never a wrong one. */
  private searchAvailable(query: QuickSearchQuery, tenantDefaults?: QuickSearchTenantDefaults | null): Observable<StockGroup[]> {
    if (!query.ambiguousSizes.length) {
      return this.lotService.getStockGroups(this.buildStockGroupFilter(query, tenantDefaults), true);
    }

    const size = query.ambiguousSizes[0]; // the common case is exactly one ambiguous mention
    // An explicit "square"/"round" keyword in the query (query.isRound) beats the matched
    // species' own type (productIsRound) — the user said it outright, no need to infer it.
    const typeKnownRound = query.isRound ?? query.productIsRound;
    const candidateFields: DimensionField[] =
      typeKnownRound === true ? ['girth'] :
      typeKnownRound === false ? ['width'] :
      ['width', 'thickness', 'girth'];

    const calls = candidateFields.map(field => {
      const tolerancePercent = field === 'girth'
        ? (tenantDefaults?.roundSearchTolerancePercent ?? DEFAULT_ROUND_TOLERANCE_PERCENT)
        : (tenantDefaults?.squareSearchTolerancePercent ?? DEFAULT_SQUARE_TOLERANCE_PERCENT);
      const band = widen(size.valueMm, tolerancePercent);
      const filter = this.buildStockGroupFilter(query, tenantDefaults);
      if (field === 'width') { filter.widthMinMm = band.min; filter.widthMaxMm = band.max; }
      if (field === 'thickness') { filter.thicknessMinMm = band.min; filter.thicknessMaxMm = band.max; }
      if (field === 'girth') { filter.girthMinMm = band.min; filter.girthMaxMm = band.max; }
      return this.lotService.getStockGroups(filter, true);
    });

    if (!calls.length) return of([]);
    return forkJoin(calls).pipe(map(results => dedupeGroups(results.flat())));
  }

  /** Same ambiguous-size fan-out as searchAvailable(), aimed at the pending-stock-in endpoint
   *  instead of stock-groups. Kept as a separate method (rather than merged into searchAvailable)
   *  since the two buckets are conceptually different — real stock vs. a "go receive this" hint —
   *  and callers may one day want to skip this bucket without touching the real-stock path. */
  private searchPendingStockIn(query: QuickSearchQuery, tenantDefaults?: QuickSearchTenantDefaults | null): Observable<PendingStockInGroup[]> {
    if (!query.ambiguousSizes.length) {
      return this.lotService.getPendingStockIn(this.buildStockGroupFilter(query, tenantDefaults), true);
    }

    const size = query.ambiguousSizes[0];
    const typeKnownRound = query.isRound ?? query.productIsRound;
    const candidateFields: DimensionField[] =
      typeKnownRound === true ? ['girth'] :
      typeKnownRound === false ? ['width'] :
      ['width', 'thickness', 'girth'];

    const calls = candidateFields.map(field => {
      const tolerancePercent = field === 'girth'
        ? (tenantDefaults?.roundSearchTolerancePercent ?? DEFAULT_ROUND_TOLERANCE_PERCENT)
        : (tenantDefaults?.squareSearchTolerancePercent ?? DEFAULT_SQUARE_TOLERANCE_PERCENT);
      const band = widen(size.valueMm, tolerancePercent);
      const filter = this.buildStockGroupFilter(query, tenantDefaults);
      if (field === 'width') { filter.widthMinMm = band.min; filter.widthMaxMm = band.max; }
      if (field === 'thickness') { filter.thicknessMinMm = band.min; filter.thicknessMaxMm = band.max; }
      if (field === 'girth') { filter.girthMinMm = band.min; filter.girthMaxMm = band.max; }
      return this.lotService.getPendingStockIn(filter, true);
    });

    if (!calls.length) return of([]);
    return forkJoin(calls).pipe(map(results => dedupePendingStockIn(results.flat())));
  }

  /** Translates the parser's field-tagged dimensionConstraints into the specific min/max params
   *  getStockGroups() understands. A plain (non-comparator) width/thickness/girth/length
   *  constraint is widened by the tenant's own configured match tolerance first — real timber
   *  rarely measures exactly to nominal size, so treating "90cm girth" (or "6ft" length) as
   *  bit-for-bit exact was itself a bug (length went unwidened until 2026-08-19 — a search
   *  ending in an exact length term routinely found nothing, even for stock whose rounded
   *  on-screen size was exactly what was typed). Comparator/range constraints (over/under/
   *  between) are left exactly as specified — the user already defined the range, tolerance
   *  would only make it looser than asked for. A "length" constraint goes to the Round-only
   *  roundLengthMinMm/MaxMm when a girth constraint is also present (a Round-scoped query,
   *  widened by the round tolerance), otherwise to the existing Square-scoped lengthMinMm/
   *  lengthMaxMm (widened by the square tolerance) — bare length-only queries apply to Square
   *  stock only, a deliberate scope limit (see quick-search-parser.ts's module doc). strictOrigin
   *  is always true here — Quick Search must never silently include unset-origin stock the way
   *  the Dispatch Wizard's own loose browsing does. */
  private buildStockGroupFilter(query: QuickSearchQuery, tenantDefaults?: QuickSearchTenantDefaults | null): StockGroupFilter {
    const filter: StockGroupFilter = {
      productId: query.productId,
      qualityCode: query.qualityCode,
      origin: query.originCode,
      strictOrigin: true,
      // Only worth asking the backend to do the extra tracing work when at least one of the two
      // pieces it feeds (price/invoice or BL number) is actually switched on.
      includeConsignmentInfo: QUICK_SEARCH_PURCHASING_FLAGS.showPriceInvoice || QUICK_SEARCH_PURCHASING_FLAGS.showBlNumber,
    };
    const hasGirth = query.dimensionConstraints.some(c => c.field === 'girth');
    const squareTolerance = tenantDefaults?.squareSearchTolerancePercent ?? DEFAULT_SQUARE_TOLERANCE_PERCENT;
    const roundTolerance = tenantDefaults?.roundSearchTolerancePercent ?? DEFAULT_ROUND_TOLERANCE_PERCENT;

    for (const c of query.dimensionConstraints) {
      switch (c.field) {
        case 'width': applyBand(filter, 'widthMinMm', 'widthMaxMm', c, squareTolerance); break;
        case 'thickness': applyBand(filter, 'thicknessMinMm', 'thicknessMaxMm', c, squareTolerance); break;
        case 'girth': applyBand(filter, 'girthMinMm', 'girthMaxMm', c, roundTolerance); break;
        case 'length': {
          // Same "only widen a plain exact value, never an explicit range" rule as width/
          // thickness/girth above (see applyBand) — length just never got this treatment
          // originally. A bare "6ft" was compared bit-for-bit against stored length, so a
          // board displaying as exactly "6ft" (already rounded for display) routinely failed
          // to match its own search term, since real cut lengths are no more exact than real
          // widths/thicknesses are. Round-scoped length reuses the round tolerance, matching
          // how girth itself is tolerance-banded on this same branch.
          const lengthTolerance = hasGirth ? roundTolerance : squareTolerance;
          let lengthMin = c.minMm;
          let lengthMax = c.maxMm;
          if (c.minMm != null && c.maxMm != null && c.minMm === c.maxMm) {
            const band = widen(c.minMm, lengthTolerance);
            lengthMin = band.min;
            lengthMax = band.max;
          }
          if (hasGirth) { filter.roundLengthMinMm = lengthMin; filter.roundLengthMaxMm = lengthMax; }
          else { filter.lengthMinMm = lengthMin; filter.lengthMaxMm = lengthMax; }
          break;
        }
      }
    }
    return filter;
  }
}

/** Only a plain exact value (min === max, i.e. no comparator/range was typed) gets the
 *  tolerance widening — a comparator or "between" already IS a deliberate range and stays as
 *  specified, tolerance would only loosen it further than the user asked for. */
function applyBand(filter: StockGroupFilter, minKey: 'widthMinMm' | 'thicknessMinMm' | 'girthMinMm',
                    maxKey: 'widthMaxMm' | 'thicknessMaxMm' | 'girthMaxMm',
                    c: DimensionConstraint, tolerancePercent: number): void {
  if (c.minMm != null && c.maxMm != null && c.minMm === c.maxMm) {
    const band = widen(c.minMm, tolerancePercent);
    filter[minKey] = band.min;
    filter[maxKey] = band.max;
  } else {
    filter[minKey] = c.minMm;
    filter[maxKey] = c.maxMm;
  }
}

function widen(valueMm: number, tolerancePercent: number): { min: number; max: number } {
  const delta = valueMm * (tolerancePercent / 100);
  return { min: Math.max(0, valueMm - delta), max: valueMm + delta };
}

/** An explicit "square"/"round" keyword only ever helped decode leftover bare numbers up to this
 *  point — it never actually excluded the other type from what comes back. Real report,
 *  2026-08-19: typing "round" alone (or "90 round") still showed Square results, because nothing
 *  removed them. Both StockGroup and PendingStockInGroup already carry their own type flag, so this
 *  is a plain client-side keep-only-matching-type pass over an already-fetched result — not a new
 *  request. No-op when the query never stated a type (isRound === null). */
function filterByType(result: QuickSearchResult, isRound: boolean | null): QuickSearchResult {
  if (isRound === null) return result;
  return {
    ...result,
    available: result.available.filter(g => g.isRound === isRound),
    pendingStockIn: result.pendingStockIn.filter(r => r.round === isRound),
  };
}

/** Round groups have no width/thickness (null); Square groups have no girth concept at all —
 *  a group's own product+site+origin+quality+width+thickness combination is unique enough to
 *  dedupe the merged results from multiple candidate-field searches without needing a
 *  server-provided id. */
function dedupeGroups(groups: StockGroup[]): StockGroup[] {
  const seen = new Set<string>();
  const result: StockGroup[] = [];
  for (const g of groups) {
    // lengthMin/Max added 2026-09-29: stock groups are now one length each — without them the
    // 4x3x7 group was dropped here as a "duplicate" of 4x3x6.
    const key = [g.productId, g.siteId, g.originCode, g.qualityCode, g.widthMm, g.thicknessMm, g.lengthMinMm, g.lengthMaxMm].join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(g);
  }
  return result;
}

/** Same shape of problem as dedupeGroups() above, and the same fix: the ambiguous-size fan-out can
 *  call the backend once per candidate field (width/thickness/girth), each returning its own
 *  already-grouped result, so the exact same underlying group can legitimately come back more than
 *  once. Unlike dedupeGroups (which just keeps the first copy — Available Now's own totals are
 *  already correct per call), a duplicate group here must be MERGED (its `units` concatenated and
 *  de-duplicated by transportUnitEncodedId, totals re-summed from the merged unit list) rather than
 *  discarded, since two different candidate-field calls can each have found a genuinely different
 *  subset of the same group's pending units. */
function dedupePendingStockIn(groups: PendingStockInGroup[]): PendingStockInGroup[] {
  const byKey = new Map<string, PendingStockInGroup>();
  for (const g of groups) {
    const key = [g.productId, g.siteId, g.originCode, g.qualityCode, g.widthMm, g.thicknessMm, g.girthMm].join('|');
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, g);
      continue;
    }
    const seenUnitIds = new Set(existing.units.map(u => u.transportUnitEncodedId));
    for (const unit of g.units) {
      if (seenUnitIds.has(unit.transportUnitEncodedId)) continue;
      seenUnitIds.add(unit.transportUnitEncodedId);
      existing.units.push(unit);
      existing.unitCount += 1;
      existing.totalCbm += unit.netCbm ?? 0;
      existing.totalPieces += unit.pieces ?? 0;
      if (unit.squareLengthMm != null) {
        if (existing.lengthMinMm == null || unit.squareLengthMm < existing.lengthMinMm) existing.lengthMinMm = unit.squareLengthMm;
        if (existing.lengthMaxMm == null || unit.squareLengthMm > existing.lengthMaxMm) existing.lengthMaxMm = unit.squareLengthMm;
      }
      if (unit.roundLengthMm != null) {
        if (existing.roundLengthMinMm == null || unit.roundLengthMm < existing.roundLengthMinMm) existing.roundLengthMinMm = unit.roundLengthMm;
        if (existing.roundLengthMaxMm == null || unit.roundLengthMm > existing.roundLengthMaxMm) existing.roundLengthMaxMm = unit.roundLengthMm;
      }
    }
  }
  return Array.from(byKey.values());
}
